import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { AppWrapper } from 'styles/common';
import Routes from 'routes/routes';

import { SUPPORTED_LANGUAGES } from 'constants/languages.constants';

import { Spinner } from 'components/Loading';
import GoogleMapsLoader from 'components/GoogleMapsLoader';

import { useAppDispatch } from 'hooks/store';
import useBranding from 'hooks/useBranding';
import useTrackPageViews from 'hooks/useTrackPageViews';
import { useFeatureFlagRefresh } from 'hooks';

import { whoami } from 'services/Authentication/thunks';
import { selectUser } from 'services/Authentication/selectors';
import { AuthUser, BrandingTypes } from 'services/resources';

const App = () => {
  const dispatch = useAppDispatch();
  const user: AuthUser = useSelector(selectUser());
  const [loading, setLoading] = useState(true);

  useBranding();

  useFeatureFlagRefresh();

  useTrackPageViews();

  useEffect(() => {
    try {
      if (window.localStorage.getItem('api_token')) {
        dispatch(whoami());
      }
    } catch {
      Sentry.addBreadcrumb({
        level: 'warning',
        category: 'browser',
        message: 'Local Storage is not available.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const getTitleTemplate = (brandingType: BrandingTypes) => {
    switch (brandingType) {
      case BrandingTypes.AVIGO:
        return '%s - Avigo';
      case BrandingTypes.WEEVE:
        return '%s - Weeve';
      default:
        return '%s - Fleetlane';
    }
  };

  return (
    <AppWrapper>
      <Helmet
        titleTemplate={getTitleTemplate(user?.profile.dealership.settings.brandingType)}
        defaultTitle="Fleetlane"
        htmlAttributes={{
          lang: user?.profile.language || SUPPORTED_LANGUAGES.EN,
        }}
      >
        <meta
          name="description"
          content="Cloud-based loaner fleet management solution built with and for car dealerships."
        />
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <>
          <Routes />
          <GoogleMapsLoader />
        </>
      )}
    </AppWrapper>
  );
};

export default App;
